import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { BookingApi } from '@/utils/api';
import { useToastContext } from '@/contexts/ToastContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';
import { ClinicTextInput } from '@/components/ClinicTextInput';

type ContactForm = {
  phoneNumber: string;
  email: string;
};

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

export function ContactInfoPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { clinic, clinicLoading, clinicError } = useClinicConfigContext();
  const { showError } = useToastContext();

  const [form, setForm] = useState<ContactForm>({ phoneNumber: '', email: '' });
  const [errors, setErrors] = useState<Partial<ContactForm>>({});
  const [isReturningPatient, setIsReturningPatient] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    const getCookie = (name: string): string | null => {
      const match = document.cookie.match(
        new RegExp(
          '(?:^|; )' +
            name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') +
            '=([^;]*)'
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    };

    const explicit = getCookie('is_returning_patient');
    const typeCookie = getCookie('patient_type');
    const returning =
      explicit !== null ? explicit === 'true' : typeCookie === 'returning';
    setIsReturningPatient(returning);

    const existingPhone = getCookie('patient_phone_number');
    const existingEmail = getCookie('patient_email');
    setForm({
      phoneNumber: returning && existingPhone ? existingPhone : '',
      email: returning && existingEmail ? existingEmail : '',
    });
  }, []);

  const setCookie = (name: string, value: string) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=604800`;
  };

  const updateField = (key: keyof ContactForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));

    // Persist to cookies immediately so the last step can read them
    if (key === 'phoneNumber') setCookie('patient_phone_number', value);
    if (key === 'email') setCookie('patient_email', value);
  };

  const isValidEmail = (value: string) => /.+@.+\..+/.test(value.trim());

  const isValid = () => {
    const newErrors: Partial<ContactForm> = {};
    if (!form.phoneNumber.trim())
      newErrors.phoneNumber = 'Phone number is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!isValidEmail(form.email)) newErrors.email = 'Enter a valid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!clinicSlug) return;
    if (!isValid()) return;

    try {
      setIsSaving(true);
      setCookie('patient_phone_number', form.phoneNumber);
      setCookie('patient_email', form.email);
      const getCookie = (name: string): string | null => {
        const match = document.cookie.match(
          new RegExp(
            '(?:^|; )' +
              name.replace(/([.$?*|{}()\\[\\]\\\\\/+^])/g, '\\$1') +
              '=([^;]*)'
          )
        );
        return match ? decodeURIComponent(match[1]) : null;
      };

      const firstName = getCookie('patient_first_name') || '';
      const lastName = getCookie('patient_last_name') || '';
      const serviceId = parseInt(getCookie('selected_service_id') || '0', 10);
      const providerId = parseInt(getCookie('selected_provider_id') || '0', 10);
      const appointmentDate = getCookie('appointment_date') || '';
      const appointmentTime = getCookie('appointment_time') || '';
      const checkoutSessionId = getCookie('checkout_session_id') || '';
      const preferredLanguage = 'en';
      const notes = getCookie('patient_notes') || '';
      const dob = getCookie('patient_dob') || '';
      const existingPatientIdStr =
        getCookie('existing_patient_id') || undefined;
      const existingPatientId = existingPatientIdStr
        ? parseInt(existingPatientIdStr, 10)
        : undefined;

      const payload = {
        patient_name: `${firstName} ${lastName}`.trim(),
        patient_phone: form.phoneNumber,
        patient_email: form.email,
        service_id: serviceId,
        provider_id: providerId,
        appointment_date: appointmentDate,
        appointment_time: appointmentTime,
        checkout_session_id: checkoutSessionId,
        preferred_language: preferredLanguage,
        notes,
        patient_dob: dob,
        existing_patient_id: existingPatientId,
      };

      let success = true;
      try {
        const result = await BookingApi.confirmBooking(clinicSlug, payload);
        success = !!result?.data?.appointment_id;
      } catch (e) {
        success = false;
      }
      if (!success) {
        showError('Failed to submit booking confirmation. Continuing.');
      }
      navigate(`/${clinicSlug}/request-submitted`);
    } finally {
      setIsSaving(false);
    }
  };

  if (clinicLoading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (clinicError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className='h-screen w-screen overflow-hidden relative flex flex-col'
      variants={pageVariants}
      initial='initial'
      animate='animate'
      exit='exit'
    >
      <motion.div className='flex-shrink-0 p-6' variants={fadeInUp}>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </motion.div>

      <div className='flex-1 flex flex-col justify-center px-6 py-8'>
        <div className='max-w-3xl mx-auto w-full'>
          <motion.div className='text-left mb-12 mt-8' variants={fadeInUp}>
            <h2
              className='text-2xl lg:text-6xl font-semibold'
              style={{ color: 'var(--clinic-text)' }}
            >
              {isReturningPatient
                ? 'Please check below and make sure their are correct.'
                : 'Please provide your phone & Email, so we can send you the appointment details.'}
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial='initial'
            animate='animate'
          >
            <motion.div className='space-y-6' variants={fadeInUp}>
              <motion.div variants={fadeInUp}>
                <ClinicTextInput
                  label='Phone Number'
                  value={form.phoneNumber}
                  onChange={v => updateField('phoneNumber', v)}
                  placeholder='your phone number'
                  error={errors.phoneNumber as string}
                  className='placeholder-clinic-text'
                />
              </motion.div>

              <motion.div variants={fadeInUp}>
                <ClinicTextInput
                  label='Email'
                  value={form.email}
                  onChange={v => updateField('email', v)}
                  placeholder='your email'
                  error={errors.email as string}
                  className='placeholder-clinic-text'
                  type='email'
                />
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div className='flex justify-start mt-12' variants={scaleIn}>
            <motion.button
              disabled={isSaving}
              onClick={handleNext}
              className={`h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5  font-semibold hover:opacity-90 ${
                isSaving ? 'opacity-80 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundColor: 'var(--clinic-secondary)',
                color: 'var(--clinic-text)',
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size='sm' text='' />
                  Saving...
                </>
              ) : (
                'Next'
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>

      <motion.div variants={fadeInUp}>
        <Footer mobileColumnLayout={true} />
      </motion.div>
    </motion.div>
  );
}

export default ContactInfoPage;
